import * as React from 'react';
import { RefObject } from 'react';
import './form-field.scss';
import { InfoPopover } from '../../info-popover/info-popover';

interface FormFieldProps extends React.HTMLAttributes<HTMLDivElement> {
    /**
     * if present - render the field label above the field
     */
    label?: string | object | React.ReactNode;
    /**
     * in case we want the label to focus on some element explicitely
     * e.g. element which is supposed to be focused is auto-generated by some external library and we don't have a control over it's ID
     */
    labelFor?: string;
    infoIconText?: string | null;
}

class FormField extends React.Component<FormFieldProps, { elementId: string }> {
    private rootRef: RefObject<HTMLDivElement>;
    private lastCapturedInput: Element;
    private idCapturingIntervalReference: number;

    public constructor(props, context) {
        super(props, context);
        this.rootRef = React.createRef<HTMLDivElement>();

        this.state = {
            elementId : props.labelFor,
        };
    }

    public componentDidMount(): void {
        if (this.props.labelFor == null) {
            // called by interval because markup inside the form field can be rebuilt
            // and this re-building is not causing any of the lifecycle events of FormField component
            this.idCapturingIntervalReference = window.setInterval(this.assignId, 1000);
        }
    }

    public componentWillUnmount(): void {
        clearInterval(this.idCapturingIntervalReference);
    }

    public render() {
        const rootFieldContainerProps = { ...this.props };
        rootFieldContainerProps.className = 'oe-form-field' + (this.props.className ? (' ' + this.props.className) : '');
        delete rootFieldContainerProps.label;
        delete rootFieldContainerProps.labelFor;
        delete rootFieldContainerProps.infoIconText;

        return (
            <div { ...rootFieldContainerProps } ref={this.rootRef}>
                {this.props.label &&
                    <label
                        className='oe-form-field__title flex'
                        htmlFor={this.state.elementId}
                        onClick={this.focusTarget}
                    >
                        {this.props.label}
                        {
                            this.props.infoIconText &&
                            <InfoPopover
                                className='m-left-8px m-bottom-8px'
                            >
                                <div>{this.props.infoIconText}</div>
                            </InfoPopover>
                        }
                    </label>
                }
                {this.props.children}
            </div>
        );
    }

    private focusTarget = () => {
        if (this.props.labelFor == null) {
            return;
        } else {
            const labeledTarget = this.rootRef.current.querySelectorAll(this.props.labelFor)[0] as HTMLElement;
            if (labeledTarget) {
                labeledTarget.focus();
            }
        }
    };

    private assignId = () => {
        const childInputs = this.rootRef.current.querySelectorAll('input,select,textarea,[contenteditable=true]');

        // check if id exists on first input across child elements. If so - assign it to label's htmlFor attribute through the state.
        // otherwise - assign auto-generated id to first child input
        const firstInput = childInputs[0];
        if (firstInput === this.lastCapturedInput) {
            return;
        }

        this.lastCapturedInput = firstInput;
        if (firstInput) {
            const firstInputId = firstInput.getAttribute('id');
            if (firstInputId && firstInputId !== this.state.elementId) {
                this.setState({ elementId: firstInputId });
            } else {
                const randomId = Math.random().toString(10).replace(/[^0-9]+/g, '');
                this.setState({ elementId: randomId});
                firstInput.setAttribute('id', randomId);
            }
        }
    };
}

export { FormField };
